import { useMemo } from 'react';

import type { NavItem, NavSection } from './SidebarNav';
import { appNavItemIdPrefix } from './useSidebarItems';

export function isInternalHomePath(pathname: string): boolean {
  return pathname === '/' || pathname.startsWith('/portal/');
}

export function shouldSidebarRedirectFromPath({
  pathname,
  sidebarLandsOnHome,
  isClient,
}: {
  pathname: string;
  sidebarLandsOnHome: boolean;
  isClient: boolean;
}): boolean {
  if (pathname === '/no-apps') {
    return true;
  }

  // A sidebar with no Dashboard (Studio layout) lands internal users on CRM, so
  // move them off every home path, not just '/'. When the sidebar keeps the
  // Dashboard as its landing item (Classic), only '/' needs the redirect and
  // SidebarNav's own `redirectPath === pathname` guard makes that a no-op.
  if (!isClient && !sidebarLandsOnHome) {
    return isInternalHomePath(pathname);
  }

  return pathname === '/';
}

export function getFirstAccessibleNavItemRedirectPath(
  navItem: NavItem,
): string {
  if (navItem.isExternalLink) {
    return `/apps?id=${navItem.htmlId.replace(appNavItemIdPrefix, '')}`;
  }

  return navItem.path;
}

/**
 * The first accessible nav item is where SidebarNav sends the user, so when that
 * destination is a home path the Dashboard is this workspace's landing page
 * (Classic sidebar). The Studio sidebar has no home item, so its first
 * destination is CRM and every home path must redirect away. Keying off the
 * sidebar the user actually gets — rather than a workspace attribute — keeps the
 * redirect correct however the studio-sidebar flag is rolled out.
 */
export function sidebarLandsOnHomePath(
  firstAccessibleNavItem: NavItem | undefined,
): boolean {
  if (!firstAccessibleNavItem) {
    return false;
  }

  return isInternalHomePath(
    getFirstAccessibleNavItemRedirectPath(firstAccessibleNavItem),
  );
}

export function useFirstAccessibleNavItem(
  mainSidebarSections: Array<NavSection>,
  isClient: boolean,
): NavItem | undefined {
  return useMemo(
    () =>
      (isClient
        ? mainSidebarSections.at(1)
        : mainSidebarSections?.at(0)
      )?.navItems.find((item) => item.canAccess && !item.isHidden),
    [mainSidebarSections, isClient],
  );
}
